/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationUsersListPage
 *
 * React page responsible for listing application-specific users with full
 * server-side pagination, search, deletion, and error handling.
 * Features:
 * - Fetches paginated user data via React Query (server-driven listing)
 * - Supports search, page transitions, and query-key based caching
 * - ProgressBarDialog shown during network loading
 * - AlertDialog used for all API-level errors
 * - Delete mutation integrated with automatic cache invalidation
 * - Navigation to “Create User” and Edit User workflows
 * - Assign Role modal
 * - View User Details modal backed by /arcee/user/<user_id>/details/
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import ApplicationUsersListComponent from '../components/applications/ApplicationUsersListComponent';
import ProgressBarDialog from '../components/common/ProgressBar';
import AlertDialog from '../components/common/AlertDialog';
import AssignRoleModal from '../components/applications/AssignRoleModal';
import ApplicationUserDetailsModal from '../components/applications/ApplicationUserDetailsModal';
import UserGroupAssignmentModal from '../components/applications/UserGroupAssignmentModal';
import { useAlertDialog } from '../components/common/AlertDialogContext';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const DEFAULT_PAGE_SIZE = 20;

export default function ApplicationUsersListPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id: applicationId } = useParams();
  const queryClient = useQueryClient();
  const { showAlertDialog } = useAlertDialog();

  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = React.useState('');
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertTitle, setAlertTitle] = React.useState('');
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertType, setAlertType] = React.useState('info');
  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = React.useState(null);
  const [groupAssignModalOpen, setGroupAssignModalOpen] = React.useState(false);
  const [selectedUserForGroup, setSelectedUserForGroup] = React.useState(null);

  const [progressOpen, setProgressOpen] = React.useState(false);
  const [progressMessage, setProgressMessage] = React.useState("");

  // --- View details modal state ---
  const [viewModalOpen, setViewModalOpen] = React.useState(false);
  const [viewLoading, setViewLoading] = React.useState(false);
  const [viewDetails, setViewDetails] = React.useState(null);
  const [viewError, setViewError] = React.useState('');
  const viewControllerRef = React.useRef(null);

  // Query
  const { data: usersData, isLoading, isError, error } = useQuery({
    queryKey: ['app-users', applicationId, page, pageSize, search],
    queryFn: async ({ signal }) => {
      if (!applicationId) return { results: [], count: 0 };

      const url = `/arcee/user/list/${applicationId}/?page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}`;
      const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
      if (!resp.ok) {
        let errBody;
        try { errBody = await resp.json(); } catch { errBody = await resp.text().catch(() => null); }
        const e = new Error('Failed to fetch users');
        e.info = errBody;
        throw e;
      }
      const body = await resp.json().catch(() => null);
      if (Array.isArray(body)) return { results: body, count: body.length };
      return {
        results: body?.results ?? body ?? [],
        count: body?.count ?? (Array.isArray(body) ? body.length : 0),
        application_id: body?.application_id,
        application_name: body?.application_name
      };
    },
    enabled: !!applicationId,
    placeholderData: keepPreviousData,
    staleTime: 5000,
    cacheTime: 2 * 60 * 1000,
    retry: 1,
  });

  // delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (userId) => {
      const resp = await apiHelper.apiCall(`/arcee/user/delete/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!resp.ok) {
        let errBody;
        try { errBody = await resp.json(); } catch { errBody = await resp.text().catch(() => null); }
        const e = new Error('Delete failed');
        e.info = errBody;
        throw e;
      }
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-users', applicationId] });
    }
  });

  function formatErrorMessage(err) {
    if (!err) return 'An unknown error occurred.';
    if (err.info) {
      if (typeof err.info === 'string') return err.info;
      if (typeof err.info === 'object') {
        const parts = [];
        for (const [k, v] of Object.entries(err.info)) {
          if (Array.isArray(v)) parts.push(`${k}: ${v.join(' ')}`);
          else parts.push(`${k}: ${String(v)}`);
        }
        return parts.join('\n');
      }
    }
    return err.message || String(err);
  }

  React.useEffect(() => {
    if (isError) {
      setAlertTitle('Failed to load users');
      setAlertMessage(formatErrorMessage(error));
      setAlertType('error');
      setAlertOpen(true);
    }
  }, [isError, error]);

  const usersList = usersData?.results ?? [];
  const totalCount = usersData?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const appName = usersData?.application_name;

  const handleCreate = () => navigate(`/dashboard/applications/${applicationId}/users/create`);
  const handleEdit = (user) => {
    navigate(`/dashboard/applications/${applicationId}/users/${user.id}/update`);
  };

  const handleAssignRole = (user) => {
    setSelectedUserForRole(user);
    setAssignModalOpen(true);
  };

  const handleAssignGroups = (user) => {
    setSelectedUserForGroup(user);
    setGroupAssignModalOpen(true);
  };

  const handleDelete = async (user) => {
    const usersList = usersData?.results ?? [];
    const totalCount = usersData?.count ?? 0;

    const indexInPage = usersList.findIndex((u) => u.id === user.id);
    const globalIndex = (page - 1) * pageSize + indexInPage + 1;

    showAlertDialog({
      title: "Confirm Deletion",
      message: `Are you sure you want to delete user "${user.username}"?`,
      type: "info",
      okLabel: "Delete",
      cancelLabel: "Cancel",
      showCancel: true,

      onOk: async () => {
        setProgressOpen(true);
        setProgressMessage("Deleting Application User...");

        try {
          // Pass application_id so the backend removes the user from THIS
          // application only, not from every app the shared identity belongs to.
          const resp = await apiHelper.apiCall(
            `/arcee/user/${user.id}/delete/${applicationId ? `?application_id=${applicationId}` : ""}`,
            {
              method: "DELETE",
              headers: { "Content-Type": "application/json" },
            }
          );

          if (!resp.ok) {
            const errBody = await resp.json().catch(() => null);

            showAlertDialog({
              title: "Deletion Failed",
              message:
                errBody?.error ||
                errBody?.message ||
                "Could not delete the application user.",
              type: "error",
              okLabel: "Close",
              showCancel: false,
            });

            return;
          }

          // Role-style page indexing
          const newIndex = Math.max(globalIndex - 2, 0);
          const newPage = Math.floor(newIndex / pageSize) + 1;

          showAlertDialog({
            title: "Success",
            message: "User deleted successfully.",
            type: "success",
            okLabel: "OK",
            showCancel: false,

            onOk: async () => {
              setPage(newPage);
              await queryClient.invalidateQueries([
                "app-users",
                applicationId,
              ]);
            },
          });
        } catch (error) {
          showAlertDialog({
            title: "Error",
            message: error.message || "Network error.",
            type: "error",
            okLabel: "Close",
            showCancel: false,
          });
        } finally {
          setProgressOpen(false);
        }
      },
    });
  };

  const handleTablePermissions = (user) => {
    navigate(`/dashboard/applications/${applicationId}/users/${user.id}/permissions`);
  };

  const handlePage = (p) => setPage(p);

  // --- View user details handler ---
  const handleViewUser = async (user) => {
    if (!user || !user.id) return;

    setViewModalOpen(true);
    setViewLoading(true);
    setViewDetails(null);
    setViewError('');

    if (viewControllerRef.current) {
      viewControllerRef.current.abort();
    }

    const controller = new AbortController();
    viewControllerRef.current = controller;

    try {
      const resp = await apiHelper.apiCall(
        `/arcee/user/${user.id}/details/${applicationId ? `?application_id=${applicationId}` : ''}`,
        {
          method: 'GET',
          signal: controller.signal,
        }
      );

      if (!resp.ok) {
        let errBody;
        try { errBody = await resp.json(); } catch { errBody = await resp.text().catch(() => null); }

        const msg =
          errBody && typeof errBody === 'object' && errBody.error
            ? errBody.error
            : typeof errBody === 'string'
              ? errBody
              : `Failed to load user details (HTTP ${resp.status}).`;

        setViewError(msg);

        // Also show global alert
        setAlertTitle('Failed to load user details');
        setAlertMessage(msg);
        setAlertType('error');
        setAlertOpen(true);

        return;
      }

      const data = await resp.json().catch(() => null);
      setViewDetails(data);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      const msg = err.message || 'Could not load user details. Please try again.';
      setViewError(msg);

      setAlertTitle('Failed to load user details');
      setAlertMessage(msg);
      setAlertType('error');
      setAlertOpen(true);
    } finally {
      setViewLoading(false);
      viewControllerRef.current = null;
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewDetails(null);
    setViewError('');
    if (viewControllerRef.current) {
      viewControllerRef.current.abort();
      viewControllerRef.current = null;
    }
  };

  React.useEffect(() => {
    return () => {
      if (viewControllerRef.current) {
        viewControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      {isLoading ? (
        <ProgressBarDialog open={true} text_message="Loading users..." cancellable={false} />
      ) : (
        <ApplicationUsersListComponent
          appName={appName}
          users={usersList}
          page={page}
          pageCount={pageCount}
          totalCount={totalCount}
          search={search}
          setSearch={(val) => { setPage(1); setSearch(val); }}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onPage={handlePage}
          onAssignRole={handleAssignRole}
          onAssignGroups={handleAssignGroups}
          onTablePermissions={handleTablePermissions}
          onView={handleViewUser}
        />
      )}

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        type={alertType}
        okLabel="OK"
        showCancel={false}
        onOk={() => setAlertOpen(false)}
        mini_mode={false}
      />

      <AssignRoleModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        applicationId={applicationId}
        user={selectedUserForRole}
      />

      <UserGroupAssignmentModal
        open={groupAssignModalOpen}
        onClose={() => setGroupAssignModalOpen(false)}
        applicationId={applicationId}
        user={selectedUserForGroup}
      />

      <ProgressBarDialog
        open={progressOpen}
        text_message={progressMessage}
        cancellable={false}
      />

      {/* View User Details Modal */}
      <ApplicationUserDetailsModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        loading={viewLoading}
        error={viewError}
        details={viewDetails}
        applicationId={applicationId}
      />
    </>
  );
}
